import React, { useMemo, useCallback } from 'react';
import { AbsoluteFill, Sequence, OffthreadVideo, useVideoConfig } from 'remotion';
import { Broll, BrollType, VideoMetadata } from '../../types';
import { getCurrentEffect } from '../../utils/helper';
import { BrollEffectVideo } from './BrollEffectVideo';

interface BrollLayerContainerProps {
  brolls: Broll[];
  videoMetadata: VideoMetadata;
  currentFrame: number;
  isRender: boolean;
  cdnBaseUrlPath: string;
  mainVideoUrl: string; // <-- add this
}

export const BrollLayerContainer: React.FC<BrollLayerContainerProps> = React.memo(
  ({ isRender, brolls, videoMetadata, currentFrame, cdnBaseUrlPath, mainVideoUrl }) => {
    const { fps } = useVideoConfig();

    const brollTransition = useMemo(
      () =>
        getCurrentEffect(
          brolls,
          videoMetadata,
          currentFrame,
          'brollEffect',
          isRender,
          cdnBaseUrlPath
        ),
      [brolls, videoMetadata, currentFrame, isRender, cdnBaseUrlPath]
    );

    const imageTransition = useMemo(
      () =>
        getCurrentEffect(
          brolls,
          videoMetadata,
          currentFrame,
          'imageEffect',
          isRender,
          cdnBaseUrlPath
        ),
      [brolls, videoMetadata, currentFrame, isRender, cdnBaseUrlPath]
    );

    const handleError = useCallback(() => {}, []);

    return (
      <AbsoluteFill>
        {brolls.map((broll, index) => {
          const layoutValue = String(broll.brollLayout);
          if (layoutValue === 'split-50-50' || layoutValue === 'split-65-35') {
            // Skip split layouts, now handled in BrollAndCaptionRenderer
            return null;
          }

          const startFrame = broll.startTime * fps;
          const durationInFrames = (broll.endTime - broll.startTime) * fps;
          if (!broll?.brollUrl || durationInFrames <= 0) return null;

          const posX = broll.brollOverlay && typeof broll.brollOverlay.positionX === 'number' ? broll.brollOverlay.positionX : 50;
          const posY = broll.brollOverlay && typeof broll.brollOverlay.positionY === 'number' ? broll.brollOverlay.positionY : 50;
          const objectPosition = `${posX}% ${posY}%`;

          // Default overlay logic (cover/contain, image/gif/video)
          return (
            <Sequence
              key={index}
              from={startFrame}
              premountFor={300}
              durationInFrames={durationInFrames}
            >
              {broll.brollType === BrollType.Video ? (
                <>
                  <OffthreadVideo
                    key={`video-${index}`}
                    muted
                    style={{
                      objectFit: broll.brollLayout === 'cover' ? 'cover' : 'contain',
                      objectPosition,
                      width: broll.brollLayout === 'contain' ? '80%' : '100%',
                      height: broll.brollLayout === 'contain' ? '80%' : '100%',
                      transform: brollTransition.transform,
                      position: broll.brollLayout === 'contain' ? 'absolute' : undefined,
                      top: broll.brollLayout === 'contain' ? '10%' : undefined,
                      left: broll.brollLayout === 'contain' ? '10%' : undefined,
                    }}
                    onError={handleError}
                    pauseWhenBuffering={true}
                    src={broll.brollUrl}
                  />
                  <BrollEffectVideo data={brollTransition} id={`${videoMetadata.id}-${index}`} />
                </>
              ) : broll.brollType === BrollType.Image ? (
                <>
                  <img
                    key={`img-${index}`}
                    style={{
                      objectFit: broll.brollLayout === 'cover' ? 'cover' : 'contain',
                      objectPosition,
                      width: broll.brollLayout === 'contain' ? '80%' : '100%',
                      height: broll.brollLayout === 'contain' ? '80%' : '100%',
                      transform: imageTransition.transform,
                      position: broll.brollLayout === 'contain' ? 'absolute' : undefined,
                      top: broll.brollLayout === 'contain' ? '10%' : undefined,
                      left: broll.brollLayout === 'contain' ? '10%' : undefined,
                    }}
                    src={broll.brollUrl}
                    alt="B-roll"
                  />
                  <BrollEffectVideo data={imageTransition} id={`${videoMetadata.id}-${index}`} />
                </>
              ) : null}
            </Sequence>
          );
        })}
      </AbsoluteFill>
    );
  }
);
